import { z } from "zod";

const PAGE_SIZE = 10;

// Keep the members in sync with `DeviceSortBy` in ../../domain/models -- the
// columns the real GET /devices can sort by.
export const deviceSortBySchema = z.enum([
  "code",
  "deviceTypeNameEn",
  "deviceTypeNameAr",
  "accountNameEn",
  "accountNameAr",
  "imei",
  "assetNameEn",
  "assetNameAr",
  "status",
  "expiryDate",
  "createdAt",
]);

// Every GET /devices query param the swagger allows, per the user's
// instruction to expose them all as filters (search itself stays narrow --
// it only ever matches status/expiryDate server-side, see DevicesListQuery).
export const devicesSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    sortBy: deviceSortBySchema.optional(),
    // No `.trim()` here -- this value round-trips through the URL on every
    // keystroke (the search input is fully controlled by router state), so
    // trimming on parse would strip a trailing space the instant it's
    // typed, making it impossible to type one at all. The actual request
    // still trims (see DevicesListScreen's own toQuery).
    search: z.string().min(1).optional(),
    deviceCode: z.string().trim().min(1).optional(),
    deviceTypeId: z.string().trim().min(1).optional(),
    accountId: z.string().trim().min(1).optional(),
    imei: z.string().trim().min(1).optional(),
    assetId: z.string().trim().min(1).optional(),
    status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
    fromExpiryDate: z.string().datetime().optional(),
    toExpiryDate: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromExpiryDate === undefined) ===
      (search.toExpiryDate === undefined),
  );

export type DevicesSearch = z.infer<typeof devicesSearchSchema>;

// `GET /devices/archived` only sorts by archive date (`sort`), so there is
// no `sortBy`. `from/toArchivedAt` are set together.
export const archivedDevicesSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedDevicesSearch = z.infer<
  typeof archivedDevicesSearchSchema
>;
